import { useState, useEffect } from "react";
import { 
  X, ArrowRight, ArrowLeft, CheckCircle, Building, Globe, Mail, Phone, User,
  Briefcase, Clock, FileText, Upload, Send, Sparkles, AlertCircle
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ServiceRequestBuilder = ({ onClose, prefilledData = {} }) => {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [formData, setFormData] = useState({
    // Contact Info
    contact_name: "",
    organization: "",
    email: "",
    phone: "",
    country: "Jamaica",
    
    // Service Details
    service_type: "",
    industry: "",
    certifications_of_interest: [],
    
    // Current State
    current_certifications: [],
    has_management_system: false,
    employees_count: "",
    
    // Timeline & Budget
    urgency: "medium",
    preferred_timeline: "",
    budget_range: "",
    
    // Additional Info
    specific_requirements: "",
    how_did_you_hear: "",
    preferred_contact_method: "email",
    
    ...prefilledData
  });

  const totalSteps = 5;

  useEffect(() => {
    // Load session data from previous tools
    loadSessionData();
  }, []);

  const loadSessionData = async () => {
    const sessionId = localStorage.getItem("chatbot_session_id");
    if (sessionId) {
      try {
        const res = await axios.get(`${API}/tools/session/${sessionId}`);
        if (res.data.tool_results) {
          setSessionData(res.data.tool_results);
          
          // Pre-fill from certification finder
          if (res.data.tool_results.certification_finder) {
            const certData = res.data.tool_results.certification_finder;
            if (certData.recommendations) {
              const topCerts = certData.recommendations.slice(0, 3).map(r => r.standard);
              setFormData(prev => ({
                ...prev,
                certifications_of_interest: topCerts
              }));
            }
          }
          
          // Pre-fill from readiness assessment
          if (res.data.tool_results.readiness_assessment) {
            const readinessData = res.data.tool_results.readiness_assessment;
            if (readinessData.target_certification) {
              setFormData(prev => ({
                ...prev,
                certifications_of_interest: [...new Set([...prev.certifications_of_interest, readinessData.target_certification.split(' - ')[0]])]
              }));
            }
          }
        }
      } catch (error) {
        console.error("Failed to load session data:", error);
      }
    }
  };

  const serviceTypes = [
    { id: "consulting", name: "Consulting & Implementation", icon: Briefcase, desc: "Gap assessment, system design, implementation support" },
    { id: "training", name: "Training", icon: FileText, desc: "Public courses, in-house training, workshops" },
    { id: "audit", name: "Auditing Services", icon: CheckCircle, desc: "Internal audits, supplier audits, pre-certification reviews" },
    { id: "certification_support", name: "Certification Support", icon: Globe, desc: "Full certification journey support" }
  ];

  const industries = [
    "Food Manufacturing",
    "Farms & Primary Production",
    "Packing & Distribution",
    "Hospitality & Food Service",
    "Other Manufacturing",
    "Service Organization",
    "Government & Public Sector",
    "Healthcare"
  ];

  const certifications = [
    "ISO 9001", "ISO 22000", "FSSC 22000", "HACCP", "GlobalG.A.P.", 
    "ISO 27001", "ISO 31000", "ISO 45001", "SQF", "BRC", "Other"
  ];

  const countries = [
    "Jamaica", "Trinidad & Tobago", "Barbados", "Bahamas", "Guyana",
    "Suriname", "Belize", "Other Caribbean", "United States", "Canada", "Other"
  ];

  const employeeCounts = [
    "1-10", "11-50", "51-100", "101-250", "251-500", "500+"
  ];

  const urgencyLevels = [
    { id: "low", name: "Low", desc: "No immediate deadline, planning ahead" },
    { id: "medium", name: "Medium", desc: "Within 3-6 months" },
    { id: "high", name: "High", desc: "Within 1-3 months" },
    { id: "urgent", name: "Urgent", desc: "Immediate need, within weeks" }
  ];

  const referralSources = [
    "Google Search", "LinkedIn", "Referral", "Previous Client", 
    "Industry Event", "PECB Website", "Other"
  ];

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const payload = {
        ...formData,
        tool_results: sessionData
      };
      
      const res = await axios.post(`${API}/tools/service-request`, payload);
      setResult(res.data);
    } catch (error) {
      console.error("Error:", error);
      alert("Failed to submit request. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.service_type !== "";
      case 2:
        return formData.industry !== "" && formData.certifications_of_interest.length > 0;
      case 3:
        return formData.contact_name !== "" && formData.organization !== "" && 
               formData.email !== "" && formData.country !== "";
      case 4:
        return formData.urgency !== "";
      case 5:
        return true;
      default:
        return false;
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">What type of support do you need?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Select the primary service you&apos;re interested in. We can discuss additional needs during consultation.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {serviceTypes.map((service) => (
                <button
                  key={service.id}
                  onClick={() => updateField("service_type", service.id)}
                  className={`p-5 rounded-xl border-2 text-left transition-all hover:border-brand ${
                    formData.service_type === service.id ? 'border-brand bg-brand/5 ring-2 ring-brand/20' : 'border-slate-200'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg ${formData.service_type === service.id ? 'bg-brand text-white' : 'bg-slate-100'}`}>
                      <service.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <div className="font-semibold">{service.name}</div>
                      <div className="text-sm text-muted-foreground mt-1">{service.desc}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Tell us about your organization</h3>
              <p className="text-sm text-muted-foreground mb-4">
                This helps us tailor our recommendations to your specific context.
              </p>
            </div>
            
            {/* Industry Selection */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Industry *</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {industries.map((ind) => (
                  <button
                    key={ind}
                    onClick={() => updateField("industry", ind)}
                    className={`p-3 rounded-lg border text-sm text-center transition-all ${
                      formData.industry === ind 
                        ? 'border-brand bg-brand text-white' 
                        : 'border-slate-200 hover:border-brand/50'
                    }`}
                  >
                    {ind}
                  </button>
                ))}
              </div>
            </div>

            {/* Certifications of Interest */}
            <div>
              <Label className="text-sm font-medium mb-2 block">
                Certifications of Interest *
                {sessionData?.certification_finder && (
                  <Badge variant="secondary" className="ml-2 text-xs">Pre-filled from your assessment</Badge>
                )}
              </Label>
              <div className="flex flex-wrap gap-2">
                {certifications.map((cert) => (
                  <button
                    key={cert}
                    onClick={() => toggleArrayField("certifications_of_interest", cert)}
                    className={`px-4 py-2 rounded-full border text-sm transition-all ${
                      formData.certifications_of_interest.includes(cert)
                        ? 'bg-brand text-white border-brand'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-brand'
                    }`}
                  >
                    {cert}
                  </button>
                ))}
              </div>
            </div>

            {/* Current Certifications */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Current Certifications (if any)</Label>
              <div className="flex flex-wrap gap-2">
                {certifications.filter(c => c !== "Other").map((cert) => (
                  <button
                    key={cert}
                    onClick={() => toggleArrayField("current_certifications", cert)}
                    className={`px-3 py-1.5 rounded-full border text-xs transition-all ${
                      formData.current_certifications.includes(cert)
                        ? 'bg-green-500 text-white border-green-500'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-green-300'
                    }`}
                  >
                    {cert}
                  </button>
                ))}
              </div>
            </div>

            {/* Has Management System */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => updateField("has_management_system", !formData.has_management_system)}
                className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                  formData.has_management_system 
                    ? 'bg-brand border-brand' 
                    : 'border-slate-300'
                }`}
              >
                {formData.has_management_system && <CheckCircle className="h-4 w-4 text-white" />}
              </button>
              <span className="text-sm">We already have a documented management system in place</span>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Contact Information</h3>
              <p className="text-sm text-muted-foreground mb-4">
                How can we reach you to discuss your requirements?
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact_name">Your Name *</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="contact_name"
                    value={formData.contact_name}
                    onChange={(e) => updateField("contact_name", e.target.value)}
                    className="pl-10"
                    placeholder="John Smith"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="organization">Organization *</Label>
                <div className="relative mt-1">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="organization"
                    value={formData.organization}
                    onChange={(e) => updateField("organization", e.target.value)}
                    className="pl-10"
                    placeholder="Company Name Ltd."
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email Address *</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateField("email", e.target.value)}
                    className="pl-10"
                    placeholder="john@company.com"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => updateField("phone", e.target.value)}
                    className="pl-10"
                    placeholder="+1 876 555 0123"
                  />
                </div>
              </div>

              <div>
                <Label>Country/Region *</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {countries.slice(0, 6).map((country) => (
                    <button
                      key={country}
                      onClick={() => updateField("country", country)}
                      className={`p-2 rounded-lg border text-xs transition-all ${
                        formData.country === country 
                          ? 'border-brand bg-brand text-white' 
                          : 'border-slate-200 hover:border-brand/50'
                      }`}
                    >
                      {country}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {countries.slice(6).map((country) => (
                    <button
                      key={country}
                      onClick={() => updateField("country", country)}
                      className={`p-2 rounded-lg border text-xs transition-all ${
                        formData.country === country 
                          ? 'border-brand bg-brand text-white' 
                          : 'border-slate-200 hover:border-brand/50'
                      }`}
                    >
                      {country}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label>Organization Size</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {employeeCounts.map((count) => (
                    <button
                      key={count}
                      onClick={() => updateField("employees_count", count)}
                      className={`p-2 rounded-lg border text-xs transition-all ${
                        formData.employees_count === count 
                          ? 'border-brand bg-brand text-white' 
                          : 'border-slate-200 hover:border-brand/50'
                      }`}
                    >
                      {count}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Timeline & Priority</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Understanding your timeline helps us prioritize and plan resources effectively.
              </p>
            </div>

            {/* Urgency Level */}
            <div>
              <Label className="text-sm font-medium mb-3 block">How urgent is this project? *</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {urgencyLevels.map((level) => (
                  <button
                    key={level.id}
                    onClick={() => updateField("urgency", level.id)}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      formData.urgency === level.id 
                        ? 'border-brand bg-brand/5' 
                        : 'border-slate-200 hover:border-brand/50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${
                        level.id === 'urgent' ? 'bg-red-500' :
                        level.id === 'high' ? 'bg-orange-500' :
                        level.id === 'medium' ? 'bg-amber-500' : 'bg-green-500'
                      }`} />
                      <span className="font-medium">{level.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{level.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Preferred Timeline */}
            <div>
              <Label htmlFor="timeline">Specific Timeline or Deadline (optional)</Label>
              <Input
                id="timeline"
                value={formData.preferred_timeline}
                onChange={(e) => updateField("preferred_timeline", e.target.value)}
                className="mt-1"
                placeholder="e.g., Need certification by Q3 2026, Audit scheduled for March"
              />
            </div>

            {/* Budget Range */}
            <div>
              <Label>Budget Range (optional)</Label>
              <div className="grid grid-cols-4 gap-2 mt-1">
                {["< $5,000", "$5,000-$15,000", "$15,000-$30,000", "$30,000+"].map((range) => (
                  <button
                    key={range}
                    onClick={() => updateField("budget_range", range)}
                    className={`p-2 rounded-lg border text-xs transition-all ${
                      formData.budget_range === range 
                        ? 'border-brand bg-brand text-white' 
                        : 'border-slate-200 hover:border-brand/50'
                    }`}
                  >
                    {range}
                  </button>
                ))}
              </div>
            </div>

            {/* Preferred Contact Method */}
            <div>
              <Label>Preferred Contact Method</Label>
              <div className="flex gap-2 mt-1">
                {[
                  { id: "email", label: "Email" },
                  { id: "phone", label: "Phone" },
                  { id: "whatsapp", label: "WhatsApp" }
                ].map((method) => (
                  <button
                    key={method.id}
                    onClick={() => updateField("preferred_contact_method", method.id)}
                    className={`px-4 py-2 rounded-lg border text-sm transition-all ${
                      formData.preferred_contact_method === method.id 
                        ? 'border-brand bg-brand text-white' 
                        : 'border-slate-200 hover:border-brand/50'
                    }`}
                  >
                    {method.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Additional Details</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Any specific requirements or context that would help us understand your needs better.
              </p>
            </div>

            {/* Specific Requirements */}
            <div>
              <Label htmlFor="requirements">Specific Requirements or Challenges</Label>
              <Textarea
                id="requirements"
                value={formData.specific_requirements}
                onChange={(e) => updateField("specific_requirements", e.target.value)}
                className="mt-1 min-h-[120px]"
                placeholder="Describe any specific challenges, requirements, or context that would help us understand your needs..."
              />
            </div>

            {/* How did you hear about us */}
            <div>
              <Label>How did you hear about CompLy CMS?</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {referralSources.map((source) => (
                  <button
                    key={source}
                    onClick={() => updateField("how_did_you_hear", source)}
                    className={`px-3 py-1.5 rounded-full border text-xs transition-all ${
                      formData.how_did_you_hear === source
                        ? 'bg-brand text-white border-brand'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-brand'
                    }`}
                  >
                    {source}
                  </button>
                ))}
              </div>
            </div>

            {/* Summary of Previous Tool Results */}
            {sessionData && (Object.keys(sessionData).length > 0) && (
              <div className="bg-brand/5 border border-brand/20 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="h-4 w-4 text-brand" />
                  <span className="font-medium text-sm">Your Assessment Results (attached)</span>
                </div>
                <div className="space-y-2 text-sm">
                  {sessionData.certification_finder && (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Certification Finder completed</span>
                    </div>
                  )}
                  {sessionData.readiness_assessment && (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Readiness Assessment: {sessionData.readiness_assessment.readiness_level?.replace('_', ' ')}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Review Summary */}
            <div className="bg-slate-50 rounded-xl p-4">
              <h4 className="font-medium mb-3">Request Summary</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground">Service:</span>
                  <span className="ml-2 font-medium">{serviceTypes.find(s => s.id === formData.service_type)?.name}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Industry:</span>
                  <span className="ml-2 font-medium">{formData.industry}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Contact:</span>
                  <span className="ml-2 font-medium">{formData.contact_name}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Organization:</span>
                  <span className="ml-2 font-medium">{formData.organization}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Country:</span>
                  <span className="ml-2 font-medium">{formData.country}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Urgency:</span>
                  <span className="ml-2 font-medium capitalize">{formData.urgency}</span>
                </div>
              </div>
              {formData.certifications_of_interest.length > 0 && (
                <div className="mt-3 pt-3 border-t">
                  <span className="text-sm text-muted-foreground">Certifications:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {formData.certifications_of_interest.map((cert) => (
                      <Badge key={cert} variant="secondary" className="text-xs">{cert}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Success Result Screen
  if (result) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-primary mb-2">Request Submitted Successfully!</h2>
          <p className="text-muted-foreground mb-6">
            Your request number is <span className="font-mono font-bold text-brand">{result.request_number}</span>
          </p>
          
          <div className="bg-slate-50 rounded-xl p-6 text-left max-w-md mx-auto mb-6">
            <h3 className="font-semibold mb-4">What happens next?</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-brand/10 text-brand flex items-center justify-center flex-shrink-0 text-sm font-bold">1</div>
                <span className="text-sm">Your request will be reviewed within 1-2 business days</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-brand/10 text-brand flex items-center justify-center flex-shrink-0 text-sm font-bold">2</div>
                <span className="text-sm">A consultant will contact you to clarify details</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-brand/10 text-brand flex items-center justify-center flex-shrink-0 text-sm font-bold">3</div>
                <span className="text-sm">We&apos;ll propose a tailored approach or training plan</span>
              </li>
            </ul>
          </div>

          {result.recommended_services?.length > 0 && (
            <div className="bg-brand/5 border border-brand/20 rounded-xl p-4 max-w-md mx-auto mb-6">
              <h4 className="font-medium text-sm mb-2">Recommended Services</h4>
              <div className="flex flex-wrap gap-2 justify-center">
                {result.recommended_services.map((service, i) => (
                  <Badge key={i} className="bg-brand/10 text-brand border-0">{service}</Badge>
                ))}
              </div>
            </div>
          )}

          <p className="text-sm text-muted-foreground mb-6">
            A confirmation email has been sent to <span className="font-medium">{formData.email}</span>
          </p>

          <Button onClick={onClose} className="bg-brand hover:bg-brand-dark">
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-primary">Service Request Builder</h2>
          <p className="text-sm text-muted-foreground">Step {step} of {totalSteps}</p>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-slate-100 rounded-full h-2 mb-8">
        <div 
          className="bg-brand h-2 rounded-full transition-all duration-300"
          style={{ width: `${(step / totalSteps) * 100}%` }}
        />
      </div>

      {/* Step Content */}
      {renderStep()}

      {/* Navigation */}
      <div className="flex justify-between pt-6 mt-6 border-t">
        <Button 
          variant="outline" 
          onClick={() => setStep(s => s - 1)}
          disabled={step === 1}
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        
        {step < totalSteps ? (
          <Button 
            onClick={() => setStep(s => s + 1)}
            disabled={!isStepValid()}
            className="bg-brand hover:bg-brand-dark"
          >
            Continue <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        ) : (
          <Button 
            onClick={handleSubmit}
            disabled={isLoading || !isStepValid()}
            className="bg-brand hover:bg-brand-dark"
          >
            {isLoading ? (
              <>Submitting...</>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" /> Submit Request
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ServiceRequestBuilder;
